import { ChangeEvent, FormEvent, ReactElement, useState, useEffect } from 'react'
import { Button, ButtonProps, Input, Modal } from 'react-daisyui'
import { isAxiosError } from 'axios'
import CuritecButton from '@/components/Buttons/CuritecButton'
import { ValidationErrorAlert } from '@/components/Alerts'
import { useLogin } from '@/sdk-client/gen/hooks/usersHooks/useLogin'
import { useGetAndCacheProfile } from '@lib/auth/hooks'
import type { Login400 } from '@/sdk-client/gen/models/usersModels/Login'
import { setEmailAuthSession } from '@lib/auth/authProviders/emailAuthProvider'
import { createAuth, useAuth } from '@lib/auth/useAuth'
import { getDefaultAuthProvider } from '@lib/auth/AuthProvider'
import { useNavigate } from '@tanstack/react-router'
import {
  EMAIL_LOGIN_LABELS,
  EMAIL_LOGIN_ERRORS,
  ROLE_NAMES,
  ROUTES,
  AUTOCOMPLETE_VALUES
} from '@features/Login/constants'

type FormState = {
  emailOrUserName: string
  password: string
}

const INITIAL_FORM_STATE: FormState = {
  emailOrUserName: '',
  password: ''
}

const UseEmailLoginButton = ({ className, ...rest }: Omit<ButtonProps, 'onClick'>): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null)
  const [loginSuccessful, setLoginSuccessful] = useState(false)
  const loginMutation = useLogin()
  const auth = useAuth()
  const navigate = useNavigate()

  // Get profile after successful login (automatically caches it)
  const { data: profileData, isFetching: isLoadingProfile } = useGetAndCacheProfile({
    query: {
      enabled: loginSuccessful
    }
  })

  const isSubmitDisabled = loginMutation.isPending || !formState.emailOrUserName.trim() || !formState.password

  const handleChange = (key: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setFormState((prev) => ({ ...prev, [key]: value }))
  }

  const resetForm = () => {
    setFormState(INITIAL_FORM_STATE)
    setValidationErrors(null)
    loginMutation.reset()
  }

  const handleClose = () => {
    if (loginMutation.isPending || isLoadingProfile) return
    setIsOpen(false)
    resetForm()
  }

  // Handle profile data and redirect based on role
  useEffect(() => {
    if (!loginSuccessful || isLoadingProfile) return

    if (profileData?.result) {
      const userProfile = profileData.result

      // Get user role - assuming first role is used
      const userRole = userProfile.roles?.[0]?.name

      // Close modal and reset form
      setIsOpen(false)
      resetForm()

      // Redirect based on role
      if (userRole === ROLE_NAMES.ADMINISTRATOR) {
        navigate({ to: ROUTES.USER_MANAGEMENT, replace: true })
      } else {
        navigate({ to: ROUTES.APPROVALS, replace: true })
      }
    }
  }, [profileData, loginSuccessful, isLoadingProfile, navigate])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (loginMutation.isPending) return

    setValidationErrors(null)

    try {
      const response = await loginMutation.mutateAsync({
        emailOrUserName: formState.emailOrUserName.trim(),
        password: formState.password
      })

      const accessToken = response?.result?.accessToken
      if (!accessToken) {
        setValidationErrors({ [EMAIL_LOGIN_ERRORS.SERVER_ERROR_TITLE]: [EMAIL_LOGIN_ERRORS.NO_ACCESS_TOKEN] })
        return
      }

      setEmailAuthSession(response.result)
      auth.strategy = createAuth(getDefaultAuthProvider())

      // Set flag to trigger profile fetch
      setLoginSuccessful(true)
    } catch (error) {
      if (isAxiosError<Login400>(error) && error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors)
      }
    }
  }

  return (
    <>
      <CuritecButton
        {...rest}
        type='button'
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {EMAIL_LOGIN_LABELS.BUTTON_TEXT}
      </CuritecButton>

      <Modal
        open={isOpen}
        className='w-full max-w-md'
      >
        <form onSubmit={handleSubmit}>
          <Modal.Header className='flex flex-col items-start space-y-1'>
            <h3 className='text-lg font-semibold text-curitec-green'>{EMAIL_LOGIN_LABELS.MODAL_TITLE}</h3>
            <p className='text-sm text-gray-500'>{EMAIL_LOGIN_LABELS.MODAL_SUBTITLE}</p>
          </Modal.Header>

          <Modal.Body className='space-y-4'>
            <div className='flex flex-col space-y-2'>
              <label
                className='text-sm font-medium text-gray-700'
                htmlFor='emailOrUserName'
              >
                {EMAIL_LOGIN_LABELS.EMAIL_LABEL}
              </label>
              <Input
                id='emailOrUserName'
                name='emailOrUserName'
                type='text'
                value={formState.emailOrUserName}
                onChange={handleChange('emailOrUserName')}
                placeholder={EMAIL_LOGIN_LABELS.EMAIL_PLACEHOLDER}
                autoComplete={AUTOCOMPLETE_VALUES.USERNAME}
                autoFocus
                disabled={loginMutation.isPending}
                required
              />
            </div>

            <div className='flex flex-col space-y-2'>
              <label
                className='text-sm font-medium text-gray-700'
                htmlFor='password'
              >
                {EMAIL_LOGIN_LABELS.PASSWORD_LABEL}
              </label>
              <Input
                id='password'
                name='password'
                type='password'
                value={formState.password}
                onChange={handleChange('password')}
                placeholder={EMAIL_LOGIN_LABELS.PASSWORD_PLACEHOLDER}
                autoComplete={AUTOCOMPLETE_VALUES.CURRENT_PASSWORD}
                disabled={loginMutation.isPending}
                required
              />
            </div>

            <ValidationErrorAlert errors={validationErrors} />
          </Modal.Body>

          <Modal.Actions className='flex justify-end space-x-2'>
            <Button
              type='button'
              onClick={handleClose}
              disabled={loginMutation.isPending}
              color='ghost'
            >
              {EMAIL_LOGIN_LABELS.CANCEL_BUTTON}
            </Button>
            <CuritecButton
              type='submit'
              loading={loginMutation.isPending}
              disabled={isSubmitDisabled}
            >
              {EMAIL_LOGIN_LABELS.SIGN_IN_BUTTON}
            </CuritecButton>
          </Modal.Actions>
        </form>
      </Modal>
    </>
  )
}

export default UseEmailLoginButton
